import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeList from 'src/components/badge-list';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';

import { useGetUserGroups, useGetUsers } from '@/hooks/queries';
import { toSingleCell } from '@/utils/table/utils/cellUtils';
import { createIdLabelFieldPropertyFilter } from '@/utils/table/utils/filterUtils';

import type { FieldConfig, Header } from '../types';

/**
 * RSP-4302 — Viewer column config (mirrors useGetOwnersFieldConfig).
 * Expects a row with `allViewers: { label, id }[]` — caller composes
 * via getAllViewersCellValue from rbac/contributorHelper.
 */
export const useGetViewersFieldConfig = <
  T extends {
    Id: string;
    allViewers: { label: string; id: string }[];
  },
>(
  header?: Header
): FieldConfig<T> => {
  const { data: users } = useGetUsers({ queryArgs: {} });
  const { data: userGroups } = useGetUserGroups({ queryArgs: {} });
  const { t } = useTranslation(['common'], { keyPrefix: 'columns' });
  const userOptions = useMemo(
    () =>
      users?.user.map((u) => ({
        label: u.FriendlyName ?? '',
        id: u.Id ?? '',
      })) ?? [],
    [users?.user]
  );
  const headerConfig = useDeepCompareMemoize(header);
  const groupOptions = useMemo(
    () =>
      userGroups?.user_group.map((u) => ({ label: u.Name, id: u.Id })) ?? [],
    [userGroups?.user_group]
  );

  return useMemo(
    () => ({
      ...(headerConfig ?? { header: t('viewers') }),
      cell: (item) => (
        <BadgeList badges={item.allViewers.map((viewer) => viewer.label)} />
      ),
      filterOptions: {
        filteringProperties: createIdLabelFieldPropertyFilter([
          ...userOptions,
          ...groupOptions,
        ]),
        filteringOptions: [...userOptions, ...groupOptions].map((u) => ({
          value: u.id,
          label: u.label,
        })),
      },
      sortingComparator: (a, b) => {
        const viewersA = toSingleCell(a.allViewers);
        const viewersB = toSingleCell(b.allViewers);

        return viewersA.localeCompare(viewersB);
      },
      exportVal: (item) =>
        item.allViewers.map((viewer) => viewer.label || '').join(','),
    }),
    [groupOptions, t, userOptions, headerConfig]
  );
};
